/**
 * Legal page content.
 *
 * These are written to describe what this codebase ACTUALLY does — the
 * consent-gated Google Analytics in GoogleAnalytics.tsx, the reCAPTCHA v3
 * provider in main.tsx, and the Mailjet-backed contact endpoint in
 * server/routes/email.js. If any of those change, change these too.
 *
 * ⚠️ NOT LEGAL ADVICE. This is an accurate, plain-language starting point
 * drafted from the site's real behaviour. Before launch, have it reviewed by a
 * qualified advisor, and confirm the two placeholders below:
 *   • the contact address (hello@geekcow.com is assumed, not verified)
 *   • the retention period for contact-form emails (12 months is a proposal)
 * A site selling GDPR compliance is held to its own policy, so it is worth the
 * review fee.
 */

export type LegalSection = {
  heading: string;
  body: string[];
  bullets?: string[];
};

export type LegalDoc = {
  path: string;
  navLabel: string;
  title: string;
  summary: string;
  lastUpdated: string;
  sections: LegalSection[];
};

export type LegalDocId = "privacy" | "terms" | "cookies";

const LAST_UPDATED = "24 July 2026";

export const LEGAL_DOCS: Record<LegalDocId, LegalDoc> = {
  privacy: {
    path: "/privacy",
    navLabel: "Privacy",
    title: "Privacy Policy",
    summary:
      "What personal data this website collects, why, how long it is kept, and the rights you have over it under the GDPR/AVG.",
    lastUpdated: LAST_UPDATED,
    sections: [
      {
        heading: "The short version",
        body: [
          "This site collects as little as possible. There is no account system, no advertising network, no profiling and no selling of data to anyone.",
          "Two things collect personal data: the contact form, which you fill in deliberately, and analytics, which only runs if you accept cookies.",
        ],
      },
      {
        heading: "Who is responsible",
        body: [
          "GeekCow Creative Studio, a sole trader (eenmanszaak) established in Weesp, Netherlands, registered with the Dutch Chamber of Commerce under KvK number 73006637, is the controller for the personal data described here.",
          "For any question about this policy, or to exercise the rights listed below, email hello@geekcow.com. You will get an answer within one month, and usually much sooner.",
        ],
      },
      {
        heading: "What is collected, and why",
        body: ["There are three cases."],
        bullets: [
          "Contact form. When you submit it, the following is sent by email: your name, your email address, the scope options you selected, the budget range you selected, and your message. Purpose: to answer your enquiry. Legal basis: your consent, and the steps taken at your request prior to entering a contract (GDPR Art. 6(1)(a) and 6(1)(b)).",
          "Spam protection. The form is protected by Google reCAPTCHA v3, which assesses whether the submission is automated. Google receives technical information about your device and browsing in order to do this. Legal basis: legitimate interest in keeping the form usable (GDPR Art. 6(1)(f)). See the Cookie Policy for an important note about when this loads.",
          "Analytics. If — and only if — you press Accept on the cookie banner, Google Analytics is loaded and records anonymous usage such as which pages are visited and for how long. Legal basis: your consent (GDPR Art. 6(1)(a)). Press Decline and no analytics script is loaded at all.",
        ],
      },
      {
        heading: "Who else sees it",
        body: ["Personal data is shared only with the processors needed to run the service:"],
        bullets: [
          "Mailjet — delivers the contact-form email. EU-based infrastructure.",
          "Google (reCAPTCHA and, with consent, Analytics) — a US provider. Transfers rely on the EU–US Data Privacy Framework and Google's standard contractual clauses.",
          "The hosting provider serving this website.",
          "Nothing is sold, rented, or shared for advertising purposes. Ever.",
        ],
      },
      {
        heading: "How long it is kept",
        body: [
          "Contact-form emails are kept for up to 12 months after the last exchange, so a conversation can be picked up again, then deleted. If an enquiry becomes a signed project, the relevant records move under the retention obligations that apply to business administration in the Netherlands (currently seven years for tax purposes).",
          "Your cookie choice is stored in your browser's local storage, not on a server, and stays there until you clear it.",
        ],
      },
      {
        heading: "Your rights",
        body: [
          "Under the GDPR/AVG you have the right to request access to your personal data, to have it corrected or erased, to restrict or object to its processing, and to receive it in a portable format. Where processing is based on consent, you can withdraw that consent at any time, which does not affect processing carried out before you withdrew it.",
          "Email hello@geekcow.com to exercise any of these. If you believe your data has been handled improperly, you also have the right to lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens) at autoriteitpersoonsgegevens.nl.",
        ],
      },
      {
        heading: "Security",
        body: [
          "The site is served over HTTPS, sets a strict set of security headers, and the contact endpoint validates and rate-limits input. No system is perfectly secure, but the same hardening checklist applied to client projects is applied here.",
        ],
      },
      {
        heading: "Changes",
        body: [
          "If this policy changes, the date at the top of this page changes with it. Material changes will be flagged on the site rather than made quietly.",
        ],
      },
    ],
  },

  terms: {
    path: "/terms",
    navLabel: "Terms",
    title: "Terms of Service",
    summary:
      "The terms that apply to using this website, and how they relate to the separate agreement that governs any actual project.",
    lastUpdated: LAST_UPDATED,
    sections: [
      {
        heading: "Scope",
        body: [
          "These terms cover your use of geekcow.com. They do not govern client work. Any project — a website, an audit, a maintenance plan, or a freelance or interim engagement — is governed by a separate written agreement signed by both parties. Where that agreement and these terms conflict, the signed agreement wins.",
        ],
      },
      {
        heading: "Service descriptions and prices",
        body: [
          "The services described on the services page are short summaries of what we offer, provided for information. They are indicative, not offers or quotations, and the exact scope of any work is agreed with you in a written proposal.",
          "No prices are published on this site. Any price is given to you individually in a written proposal and depends on scope; the figure in your signed proposal is the one that applies. Where VAT treatment is not stated, the applicable Dutch VAT rules at the time of invoicing apply.",
        ],
      },
      {
        heading: "Availability and response times",
        body: [
          "This is a one-person studio working with a small network of specialists. Nothing on this site should be read as a promise of 24/7 support or a guaranteed response time. Service levels, if any, are set out in your signed agreement.",
          "The site itself is provided on an as-is basis. It may be unavailable for maintenance without notice.",
        ],
      },
      {
        heading: "Compliance claims",
        body: [
          "Services described as delivering security hardening, GDPR/AVG compliance or WCAG accessibility are professional services carried out to a documented checklist, and each build ships with a written compliance report.",
          "That report describes work performed. It is not legal advice and it is not a certification or a warranty that any particular law applies to you or that you are compliant with it. Whether a given regulation — including the European Accessibility Act — applies to your organisation is a question for your own legal advisor. Compliance is also not a fixed state: software, plugins and law all change, which is what the maintenance plans exist to address.",
        ],
      },
      {
        heading: "Intellectual property",
        body: [
          "The design, text, code and images on this website belong to GeekCow Creative Studio, except for client logos and project imagery, which remain the property of their respective owners and appear here with permission as portfolio work.",
          "You may not copy or reuse the content of this site without written permission. Ownership of work produced for a client is dealt with in that client's agreement.",
        ],
      },
      {
        heading: "Liability",
        body: [
          "To the extent permitted by law, GeekCow Creative Studio is not liable for indirect or consequential loss arising from your use of this website, or from reliance on the general information published on it.",
          "Liability arising from client projects is limited as set out in the applicable signed agreement. Nothing in these terms excludes liability that cannot lawfully be excluded, including liability for intent or deliberate recklessness.",
        ],
      },
      {
        heading: "Governing law",
        body: [
          "These terms are governed by Dutch law. Disputes will be submitted to the competent court in the Netherlands, unless a mandatory rule requires otherwise.",
        ],
      },
    ],
  },

  cookies: {
    path: "/cookies",
    navLabel: "Cookies",
    title: "Cookie Policy",
    summary:
      "Exactly which cookies and browser storage this site uses, what happens when you accept or decline, and how to change your mind.",
    lastUpdated: LAST_UPDATED,
    sections: [
      {
        heading: "Nothing tracking loads before you say yes",
        body: [
          "No analytics cookies are set when you arrive. The banner appears, and the analytics script is only fetched after you press Accept. Press Decline, or close the banner, and it is never loaded.",
        ],
      },
      {
        heading: "Strictly necessary storage",
        body: [
          "Two values are written to your browser's local storage regardless of your choice, because they are what remembers the choice itself:",
        ],
        bullets: [
          "cookie-consent — whether you accepted or declined. Without it, you would be asked on every page.",
          "cookie-consent-date — when you chose, so the choice can be refreshed after a reasonable period.",
        ],
      },
      {
        heading: "Analytics — only with consent",
        body: [
          "If you accept, Google Analytics is loaded and sets its own cookies (typically _ga and _ga_*, lasting up to two years) to distinguish visitors and measure which pages get used. It is used to understand traffic in aggregate. It is not used for advertising and the data is not sold.",
        ],
      },
      {
        heading: "reCAPTCHA — please read this one",
        body: [
          "The contact form is protected by Google reCAPTCHA v3. Being straight with you: the reCAPTCHA script currently initialises when the site loads, before you have made a cookie choice, rather than waiting until you interact with the form. reCAPTCHA is used strictly for spam protection and never for advertising, but that load order is stricter than we would like and is on the list to fix.",
          "If that matters to you, you can reach the studio by email instead of using the form.",
        ],
      },
      {
        heading: "Changing your mind",
        body: [
          "Clear this site's data in your browser settings and the banner will appear again on your next visit, letting you choose differently. You can also block or delete cookies entirely in your browser — the site will keep working, since nothing here depends on analytics.",
        ],
      },
      {
        heading: "No third-party embeds",
        body: [
          "There are no social media pixels, no advertising tags, no embedded video players and no chat widgets on this site. Links to LinkedIn, Instagram, GitHub and the booking calendar are ordinary links: nothing is loaded from them until you click.",
        ],
      },
    ],
  },
};
